import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { GET_CRASHBOX_EVENTS } from "./realQueries";
import { CrashEvent } from "./types";

interface RawCrashboxEvent {
  deviceEsn: string;
  crashes: CrashEvent[];
}

const LOOKBACK_HOURS = 24;

// Only fires while the Crash tab is mounted (see CrashPage) — this hook is not called from
// the dashboard shell, so switching tabs is what triggers/cancels this query.
export function useVehicleCrash(deviceEsns: string[]) {
  const startDate = useMemo(() => DateTime.now().minus({ hours: LOOKBACK_HOURS }).toUTC().toISO(), []);
  const endDate = useMemo(() => DateTime.now().toUTC().toISO(), []);

  const { data, loading, error, refetch } = useQuery<{ telematicCrashboxEvents: RawCrashboxEvent[] }>(GET_CRASHBOX_EVENTS, {
    variables: { deviceEsn: deviceEsns, startDate, endDate },
    skip: deviceEsns.length === 0,
    fetchPolicy: "cache-and-network",
  });

  const crashesByEsn = useMemo(() => {
    const grouped: Record<string, CrashEvent[]> = {};
    (data?.telematicCrashboxEvents ?? []).forEach((event) => {
      grouped[event.deviceEsn] = event.crashes ?? [];
    });
    return grouped;
  }, [data]);

  return { crashesByEsn, loading, error, refetch };
}
